import { ColumnDef } from "@tanstack/react-table";
import { EditIcon, TrashAltIcon } from "assets/icons";
import { DragDropChangeValue } from "components";
import { moveElementInArray } from "helpers";
import { useLanguage } from "hooks";
/* eslint-disable react-hooks/exhaustive-deps */
import { t } from "i18next";
import { useMemo, useState } from "react";
import {
  ItemsTableDragDrop,
  RowDragHandleCell,
  Switch,
  TableItemActionsControl
} from "ui";

import {
  useReorderUsageGuideFaqItems,
  useUpdateUsageGuideFaqItem
} from "../../apis";
import { stripHtml } from "../../helpers";
import { UsageGuideFaqItem } from "../../productUsageGuides.types";
import { UsageGuideFaqTableProps } from "./UsageGuideFaqTable.types";

import styles from "./UsageGuideFaqTable.module.scss";

const UsageGuideFaqTable = ({
  faqItems,
  isLoading = false,
  isReadOnly = false,
  onEdit,
  onDelete,
  setFaqItems
}: UsageGuideFaqTableProps) => {
  const { currentLanguage } = useLanguage();

  const reorderFaqItems = useReorderUsageGuideFaqItems();
  const updateFaqItem = useUpdateUsageGuideFaqItem();

  const [openedItemOptionsIndex, setOpenedItemOptionsIndex] = useState<
    number | null
  >(null);

  const onChangeOrder = (value: DragDropChangeValue) => {
    const { fromIndex, toIndex } = value;

    // Snapshot first: moveElementInArray rewrites `sortOrder` on the very
    // objects it was handed, so reading the old values afterwards is too late.
    const previousSortOrders = new Map(
      faqItems.map((item) => [item.id, item.sortOrder])
    );

    const reordered = moveElementInArray({
      array: faqItems,
      keyForReorder: "sortOrder",
      fromIndex,
      toIndex
    });

    setFaqItems(reordered);

    // There is no bulk endpoint, so every request here is one PATCH. Send only
    // the rows that actually moved — dragging the last item up should not
    // rewrite the ones above it that kept their place.
    const movedItems = reordered
      .filter((item) => previousSortOrders.get(item.id) !== item.sortOrder)
      .map((item) => ({ id: item.id, sortOrder: item.sortOrder }));

    if (movedItems.length === 0) {
      return;
    }

    reorderFaqItems.mutate({ items: movedItems });
  };

  const columns: Array<ColumnDef<UsageGuideFaqItem, UsageGuideFaqItem[]>> =
    useMemo(() => {
      const dragHandleColumn: ColumnDef<
        UsageGuideFaqItem,
        UsageGuideFaqItem[]
      > = {
        id: "dragHandle",
        header: "",
        enableSorting: false,
        size: 3,
        minSize: 3,
        cell: (cell) => (
          <RowDragHandleCell rowId={cell.row.id} isDragDisabled={isReadOnly} />
        )
      };

      const contentColumns: Array<
        ColumnDef<UsageGuideFaqItem, UsageGuideFaqItem[]>
      > = [
        {
          id: "question",
          header: t("Question"),
          enableSorting: false,
          size: isReadOnly ? 40 : 35,
          minSize: 35,
          cell: ({ row }) => (
            <div className={styles.faqTable_question}>
              {row.original.question}
            </div>
          )
        },
        {
          id: "answer",
          header: t("Answer"),
          enableSorting: false,
          size: isReadOnly ? 60 : 44,
          minSize: 44,
          cell: ({ row }) => (
            <div className={styles.faqTable_answer}>
              {stripHtml(row.original.answer)}
            </div>
          )
        }
      ];

      const actionColumns: Array<
        ColumnDef<UsageGuideFaqItem, UsageGuideFaqItem[]>
      > = [
        {
          id: "isEnabled",
          header: t("Visible"),
          enableSorting: false,
          size: 14,
          minSize: 14,
          cell: ({ row }) => (
            <Switch
              checked={row.original.isEnabled}
              onChange={(e) =>
                updateFaqItem.mutate({
                  id: row.original.id,
                  isEnabled: e.target.checked
                })
              }
            />
          )
        },
        {
          accessorKey: "dotsColumn",
          header: () => null,
          size: 4,
          minSize: 4,
          cell: ({ row }) => (
            <TableItemActionsControl
              actionsList={[
                {
                  label: "Edit",
                  Icon: <EditIcon fill={"#090914"} width={18} height={18} />,
                  onClick: () => onEdit(row.original)
                },
                {
                  label: "Delete",
                  Icon: (
                    <TrashAltIcon fill={"#ff0033"} width={18} height={18} />
                  ),
                  labelColor: "#ff0033",
                  onClick: () => onDelete(row.original.id)
                }
              ]}
              rowIndex={row.index}
              setOpenedItemOptionsIndex={setOpenedItemOptionsIndex}
              openedItemOptionsIndex={openedItemOptionsIndex}
            />
          ),
          enableSorting: false
        }
      ];

      return isReadOnly
        ? contentColumns
        : [dragHandleColumn, ...contentColumns, ...actionColumns];
    }, [faqItems, openedItemOptionsIndex, isReadOnly, currentLanguage]);

  return (
    <ItemsTableDragDrop
      className={styles.faqTable}
      columns={columns}
      data={faqItems}
      isLoading={isLoading}
      skeletonHeight={65}
      emptyListMessage={t("No questions yet")}
      droppableId={"usageGuideFaqItemsList"}
      keyForReorder={"sortOrder"}
      onChange={onChangeOrder}
      isDragDisabled={isReadOnly}
    />
  );
};

export default UsageGuideFaqTable;
